// Shared, cached people lookups for chat headers, sender lines, mentions and
// info screens (mobile: proPeople.js). Same freshness window as the room
// details cache in ProjectDetail.
import { callAPI } from "../helper/apiUtils";

const TTL_MS = 2 * 60 * 1000;
const roomCache = new Map(); // buildingId -> { at, data | promise }
const userCache = new Map(); // userId -> { at, data | promise }

const fresh = (entry) => !!entry && entry.data && Date.now() - entry.at < TTL_MS;

/**
 * GET /building/:id/user → { members, byId, currentMember }.
 * members: the backend rows (id, firstName, lastName, fullName, avatar,
 * position, companyName, company, …); byId: Map of String(id) → member.
 * Concurrent calls share one request. Resolves null when the call fails.
 */
export function getRoomPeople(buildingId, { force = false } = {}) {
  if (buildingId == null || buildingId === "") return Promise.resolve(null);
  const key = String(buildingId);
  const entry = roomCache.get(key);
  if (!force && fresh(entry)) return Promise.resolve(entry.data);
  if (!force && entry && entry.promise) return entry.promise;

  const promise = callAPI(`/building/${key}/user`, {}, "GET")
    .then(({ data }) => {
      if (!data || data.status === false) throw new Error((data && data.message) || "");
      const members = Array.isArray(data.members) ? data.members : Array.isArray(data.users) ? data.users : [];
      const byId = new Map(members.filter((m) => m && m.id != null).map((m) => [String(m.id), m]));
      const result = { members, byId, currentMember: data.currentMember || null };
      roomCache.set(key, { at: Date.now(), data: result });
      return result;
    })
    .catch(() => {
      roomCache.delete(key);
      return null;
    });
  roomCache.set(key, { at: 0, data: null, promise });
  return promise;
}

/** The cached room people, or null (no request). */
export function peekRoomPeople(buildingId) {
  const entry = buildingId == null ? null : roomCache.get(String(buildingId));
  return entry && entry.data ? entry.data : null;
}

/** Drop a room's entry (after adding or removing members). */
export function invalidateRoomPeople(buildingId) {
  if (buildingId != null) roomCache.delete(String(buildingId));
}

/**
 * GET /user/:id/info → { company (name), email, website, position (upper
 * case) } for a 1:1 partner's role line. Resolves null when the call fails.
 */
export function getUserInfo(userId, { force = false } = {}) {
  if (userId == null || userId === "") return Promise.resolve(null);
  const key = String(userId);
  const entry = userCache.get(key);
  if (!force && fresh(entry)) return Promise.resolve(entry.data);
  if (!force && entry && entry.promise) return entry.promise;

  const promise = callAPI(`/user/${key}/info`, {}, "GET")
    .then(({ data }) => {
      if (!data || data.status === false) throw new Error((data && data.message) || "");
      const user = data.userInfo || data.data || data.user || null;
      userCache.set(key, { at: Date.now(), data: user });
      return user;
    })
    .catch(() => {
      userCache.delete(key);
      return null;
    });
  userCache.set(key, { at: 0, data: null, promise });
  return promise;
}

/**
 * "Role · Company" line of a person (mobile proPeople roleLine): position
 * (localised by the caller via `formatPosition`) and company name, whichever
 * exist. Takes /building/:id/user members and /user/:id/info records.
 */
export function roleLineOf(person, formatPosition = (p) => p) {
  if (!person) return "";
  const position = person.position ? formatPosition(person.position) : "";
  const company =
    person.companyName ||
    (typeof person.company === "string" ? person.company : person.company && person.company.name) ||
    "";
  return [position, company].filter(Boolean).join(" · ");
}
